import PulsatingDots from '@/components/PulsatingDots';
import useStore from '@/store/use-store';
import { fDate } from '@/utils/formatTime';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
import {
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import Scrollbar from '../../Scrollbar';
import FuelConsumptionModal from './FuelConsumptionModal';

const DetailCard = styled(Card)(({ theme }) => ({
  ...theme.typography.h4,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  textAlign: 'center'
}));

export default function FuelConsumptionList() {
  const { t } = useTranslation();
  const loading = useStore((state) => state.loadingQuarterlyFuelConsumptions);
  const fuelConsumptions = useStore((state) => state.quarterlyFuelConsumptions);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const onAddClick = (request) => {
    setCurrentRequest(request);
    setModalIsOpen(true);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - fuelConsumptions.length) : 0;
  const isMeasurementsEmpty = fuelConsumptions.length === 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const CardHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4)
  }));

  const LabelStyle = styled(Typography)(({ theme }) => ({
    ...theme.typography.subtitle2,
    color: theme.palette.text.secondary
  }));

  return (
    <DetailCard sx={{ p: 3 }}>
      <CardHeader>
        <LabelStyle>{t('fuel_consumption.required_consumption')}</LabelStyle>
      </CardHeader>
      <CardContent sx={{ p: 0 }}>
        <Typography sx={{ pb: 2 }} variant="body2" align="left">
          {t('fuel_consumption.required_consumption_explanation')}
        </Typography>
        <FuelConsumptionModal
          close={() => setModalIsOpen(false)}
          isOpen={modalIsOpen}
          request={currentRequest}
        />
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ship.title')}</TableCell>
                  <TableCell>{t('fuel_consumption.requested_quarter')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              {!loading && (
                <TableBody>
                  {fuelConsumptions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { ship, title, beginDate, endDate } = row;
                      return (
                        <TableRow hover key={`${ship.id}-${title}`} tabIndex={-1}>
                          <TableCell component="th" scope="row">
                            <Typography
                              to={`/client/ship/${ship.id}`}
                              component={RouterLink}
                              variant="subtitle2"
                              noWrap
                            >
                              {ship.title}
                            </Typography>
                          </TableCell>
                          <TableCell component="th" scope="row">
                            <Typography variant="subtitle2" noWrap>
                              {title} ({fDate(beginDate)} - {fDate(endDate)})
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              onClick={() => onAddClick(row)}
                              aria-label={t('label.add_fuel_consumption')}
                            >
                              <Icon icon={plusFill} />
                              <span className="sr-only">{t('label.add_fuel_consumption')}</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              )}
              {loading && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={3} sx={{ py: 3 }}>
                      <Paper>
                        <PulsatingDots size={15} />
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
              {isMeasurementsEmpty && !loading && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={3} sx={{ py: 3 }}>
                      <Paper>
                        <Typography gutterBottom align="center" variant="subtitle1">
                          {t('label.no_labels')}
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={fuelConsumptions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
    </DetailCard>
  );
}
